import { Injectable } from '@angular/core';
import { LoggingChannels, LoggingService } from '../logging/logging.service';
import { IntervalService } from '../interval/interval.service';
import { AnalyticEventService } from '../analytic-event/analytic-event.service';
import { NotificationService } from '../notification/notification.service';
import { BlurCpu } from './blur-cpu';
import { FeatureName, SupportService } from '../support/support.service';
import { BlurGpu } from './blur-gpu';
import { VBQualityController } from './vb-quality-controller';
import { VBMaskProcessor } from './vb-mask-processor';

declare global {
  interface Window {
    crewdleShowMask: (show: boolean) => void;
    crewdleChangeThreshold: (certainty: number) => void;
    crewdleChangeResolution: (resolution: number) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class VirtualBackgroundService {
  public blurred: boolean;
  public blurDensityState: number;
  public backgroundImage: HTMLImageElement;
  public backgroundImg: boolean;
  public isActive: boolean;
  public resolution: number;
  public savedResolution: number;
  public forceResolution: boolean;
  private loopInterval;
  private qualityController: VBQualityController;
  private maskProcessor: VBMaskProcessor;

  constructor(
    private logging: LoggingService,
    private interval: IntervalService,
    private analytics: AnalyticEventService,
    private notification: NotificationService,
    private support: SupportService,
  ) {
    this.blurred = false;
    this.backgroundImg = false;
    this.isActive = false;
    this.backgroundImage = new Image();

    this.qualityController = new VBQualityController(
      this.logging,
      this.notification,
      () => this.isActive,
      () => this.resetInterval(),
      (resolution: number) => { this.resolution = resolution; },
    );

    this.maskProcessor = new VBMaskProcessor(
      this.logging,
      this.support,
      () => ({
        blurred: this.blurred,
        backgroundImg: this.backgroundImg,
        backgroundImage: this.backgroundImage,
        blurDensityState: this.blurDensityState,
        isActive: this.isActive,
        resolution: this.resolution,
      }),
      (active: boolean) => { this.isActive = active; },
    );

    window.crewdleShowMask = (show: boolean) => {
      this.maskProcessor.showMask = show;
      if (show) {
        document.getElementById('home-video').style.padding = '0';
      } else {
        document.getElementById('home-video').style.padding = '10px';
      }
    };
    window.crewdleChangeThreshold = (certainty: number) => {
      this.maskProcessor.certaintyThreshold = certainty;
    };
    window.crewdleChangeResolution = (resolution: number) => {
      this.forceResolution = true;
      this.qualityController.forceResolution = true;
      this.resolution = resolution;
      this.qualityController.resolution = resolution;
    };
    if (this.support.isFeatureSupported(FeatureName.WebGL)) {
      this.analytics.logEvent('virtual_backgrounds_gpu');
      this.maskProcessor.blur = new BlurGpu();
    } else {
      this.analytics.logEvent('virtual_backgrounds_cpu');
      this.maskProcessor.blur = new BlurCpu();
    }
    this.maskProcessor.offscreenCanvas = document.createElement('canvas');
    this.maskProcessor.offscreenContext = this.maskProcessor.offscreenCanvas.getContext('2d');
    this.maskProcessor.showMask = false;
    this.maskProcessor.certaintyThreshold = 0.5;
    this.maskProcessor.createSegmenter();
  }

  public async startBlur(blurDensity?: string, imageUrl?: string) {
    this.analytics.logEvent('virtual_backgrounds');
    this.stopBlur();

    this.resolution = 0.5;
    this.qualityController.resolution = 0.5;
    this.qualityController.fps = 24;
    this.qualityController.lastDecrease = (new Date()).getTime();

    if (!this.maskProcessor.segmenter) {
      await this.maskProcessor.createSegmenter();
    }

    if (blurDensity) {
      this.backgroundImg = false;
      this.blurred = true;
      this.backgroundImage.src = '';
      this.blurDensityState = blurDensity === 'strong' ? 4 : 2;
      this.loopInterval = setInterval(() => this.maskProcessor.handleBlur(), 1000 / this.qualityController.fps);
      this.logging.log('VirtualBackgroundService: Start blur', LoggingChannels.VirtualBackground);
    }

    if (imageUrl) {
      this.blurred = false;
      this.backgroundImg = true;
      this.backgroundImage.src = imageUrl;
      this.loopInterval = setInterval(() => this.maskProcessor.handleBackgroundImg(), 1000 / this.qualityController.fps);
      this.logging.log('VirtualBackgroundService: Start background image', LoggingChannels.VirtualBackground);
    }

    this.interval.setInterval('virtual-background-upgrade', () => this.qualityController.upgrade(), 5000);

    await this.isReady();
  }

  public stopBlur() {
    this.interval.clearInterval('virtual-background-upgrade');
    if (this.loopInterval) {
      clearInterval(this.loopInterval);
    }
    this.loopInterval = null;
    this.maskProcessor.clearCanvases();
    this.resolution = null;
    this.qualityController.resolution = null;
    this.isActive = false;
    this.blurred = false;
    this.backgroundImg = false;
    this.logging.log('VirtualBackgroundService: Stop blur', LoggingChannels.VirtualBackground);
  }

  public getVideoTrack(): MediaStreamTrack {
    const stream = this.maskProcessor.offscreenCanvas.captureStream(24);
    const video = stream.getVideoTracks();
    if (video.length > 0) {
      return video[0];
    }
    return null;
  }

  public downgrade(): boolean {
    return this.qualityController.downgrade();
  }

  private async isReady() {
    if (this.isActive) {
      return true;
    }
    await this.interval.sleep();
    return this.isReady();
  }

  private resetInterval() {
    clearInterval(this.loopInterval);
    if (this.blurred) {
      this.loopInterval = setInterval(() => this.maskProcessor.handleBlur(), 1000 / this.qualityController.fps);
    } else {
      this.loopInterval = setInterval(() => this.maskProcessor.handleBackgroundImg(), 1000 / this.qualityController.fps);
    }
  }
}
